// ANGEL FLIGHT — undead catalog (per-type stats) + pure helpers.
//
// Zombies advance toward the camera in world-depth (z). `speed` is metres/sec
// of closing distance. `widthM`/`heightM` are world sizes (the projection scales
// them by depth). Pure — no Phaser.

import type { BodyZone, EnemyId, Faction } from '../core/types';

/**
 * How an enemy closes on the gunner. The EnemySystem branches on this each frame.
 * Absence is treated as `march` (the legacy straight advance + weave).
 *  - `march`  — straight advance + idle lateral weave (filler horde).
 *  - `flank`  — drifts its x toward the nearest lateral edge as it closes, so it
 *               tries to come at you from the side.
 *  - `rush`   — marches normally until close, then accelerates into a lunge.
 *  - `shoot`  — advances to a standoff depth, STOPS, telegraphs, then fires a
 *               straight projectile at the gunner on a cooldown (RPG soldier).
 *  - `lob`    — like `shoot` but a slower, lower-damage arcing projectile (spit).
 */
export type EnemyBehavior = 'march' | 'flank' | 'rush' | 'shoot' | 'lob';

export interface EnemyDef {
  id: EnemyId;
  faction: Faction;
  name: string;
  hp: number;
  /** Closing speed toward the gunner (metres/sec). */
  speed: number;
  /** Damage dealt to the gunner on reaching the strike plane. */
  contactDamage: number;
  bounty: number;
  /** SALVAGE (hard currency) dropped on death. Absent/0 = none — only the
   *  heavy threats (elites, future bosses) pay it, so salvage stays the scarce
   *  workshop currency. Difficulty-scaled at award time (EconomySystem). */
  salvage?: number;
  /** World size (metres). */
  widthM: number;
  heightM: number;
  /** Headshot multiplier. */
  headMult: number;
  tier: 'grunt' | 'fast' | 'elite';
  /** Texture variant key suffix. */
  skin: 'shambler' | 'runner' | 'brute';

  /* ── behaviour (optional; absence = `march`) ───────────────────────────── */

  /** Tactical movement/attack profile. See EnemyBehavior. */
  behavior?: EnemyBehavior;
  /** `shoot`/`lob`: depth (metres) at which it halts to open fire. */
  standoffZ?: number;
  /** `shoot`/`lob`: ms between fired projectiles once at standoff. */
  fireIntervalMs?: number;
  /** `shoot`/`lob`: telegraph wind-up (ms) before the FIRST shot — the tell the
   *  render layer glows so the player can react. */
  telegraphMs?: number;
  /** `shoot`/`lob`: projectile closing speed toward the gunner (metres/sec). */
  projectileSpeed?: number;
  /** `shoot`/`lob`: hull damage the projectile deals if it reaches the strike
   *  plane (defaults to `contactDamage` when absent). */
  projectileDamage?: number;
  /** `shoot`: its projectiles are heat-seeking rockets the gunner can DECOY
   *  with flares (key F). Bullets/globs are not flare-able. */
  flareable?: boolean;
  /** `rush`: depth (metres) within which the charge lunge engages. */
  rushRangeZ?: number;
  /** `rush`: speed multiplier at full lunge (ramps in from 1 over the range). */
  rushSpeedMult?: number;
}

export const ENEMIES: Readonly<Record<EnemyId, EnemyDef>> = Object.freeze({
  shambler: {
    id: 'shambler',
    faction: 'undead',
    name: 'Shambler',
    hp: 22,
    speed: 2.0,
    contactDamage: 7,
    bounty: 2,
    widthM: 0.7,
    heightM: 1.8,
    headMult: 2.2,
    tier: 'grunt',
    skin: 'shambler',
    // Filler horde — straight advance + idle weave.
    behavior: 'march',
  },
  runner: {
    id: 'runner',
    faction: 'undead',
    name: 'Runner',
    hp: 14,
    speed: 3.6,
    contactDamage: 9,
    bounty: 3,
    widthM: 0.6,
    heightM: 1.7,
    headMult: 2.2,
    tier: 'fast',
    skin: 'runner',
    // Sprinter — accelerates into a lunge once it's close. 3.6 m/s (was 5.2)
    // + a tighter, gentler lunge: at 5.2 + the world drift a runner crossed
    // spawn-to-strike in under 3 seconds — gone before the player could even
    // track it (the playtest "they rush past before I can react"). It is still
    // the FAST tier (~2× a shambler) and the close-in lunge keeps the scare.
    behavior: 'rush',
    rushRangeZ: 7,
    rushSpeedMult: 1.9,
  },
  brute: {
    id: 'brute',
    faction: 'undead',
    name: 'Brute',
    hp: 130,
    speed: 1.5,
    contactDamage: 22,
    bounty: 14,
    // Elite → drops salvage for the field-workshop retrofit.
    salvage: 2,
    widthM: 1.3,
    heightM: 2.4,
    headMult: 3,
    tier: 'elite',
    skin: 'brute',
    // Tanky wall — just marches; soaks fire while the rest flank.
    behavior: 'march',
  },
  spitter: {
    id: 'spitter',
    faction: 'undead',
    name: 'Spitter',
    hp: 26,
    speed: 1.6,
    contactDamage: 5,
    bounty: 5,
    widthM: 0.7,
    heightM: 1.7,
    headMult: 2,
    tier: 'grunt',
    skin: 'shambler',
    // Ranged acid — halts mid-field, lobs a slow arcing glob at the bird.
    behavior: 'lob',
    standoffZ: 15,
    telegraphMs: 900,
    fireIntervalMs: 2600,
    projectileSpeed: 7,
    projectileDamage: 6,
  },
  screamer: {
    id: 'screamer',
    faction: 'undead',
    name: 'Screamer',
    hp: 40,
    speed: 2.4,
    contactDamage: 6,
    bounty: 8,
    // Elite RPG → drops salvage (the priority kill also funds the workshop).
    salvage: 2,
    widthM: 0.7,
    heightM: 1.8,
    headMult: 2,
    tier: 'elite',
    skin: 'runner',
    // RPG soldier — advances to standoff, telegraphs, then fires fast rockets at
    // range. The priority target: hurts you from afar, so kill it first — or
    // pop FLARES (key F) while its rocket is in flight to decoy it.
    behavior: 'shoot',
    standoffZ: 12,
    telegraphMs: 700,
    fireIntervalMs: 1800,
    projectileSpeed: 14,
    projectileDamage: 12,
    flareable: true,
  },
  marauder: {
    id: 'marauder',
    faction: 'undead',
    name: 'Marauder',
    hp: 20,
    speed: 2.8,
    contactDamage: 6,
    bounty: 6,
    widthM: 0.65,
    heightM: 1.8,
    headMult: 2.4,
    tier: 'grunt',
    skin: 'runner',
    // Hostile rifleman — halts deep and hammers the bird with fast, low-damage
    // rounds on a quick cadence. Chip pressure that stacks up in squads; bullets
    // can NOT be flared (flares only decoy rockets), so he must be shot.
    behavior: 'shoot',
    standoffZ: 18,
    telegraphMs: 500,
    fireIntervalMs: 650,
    projectileSpeed: 24,
    projectileDamage: 2,
  },
  trooper: {
    id: 'trooper',
    // BLUEFOR friendly (AC-130 "DON'T HIT FRIENDLIES"). It advances among the
    // horde like an enemy and IS raycast-hittable — that's the point: you CAN
    // shoot it, and you must not. It deals NO damage to the gunner (contactDamage
    // 0) and pays NO bounty; reaching the strike plane it just walks off the lane
    // (EnemySystem despawns it harmlessly). FiringSystem reads faction==='civilian'
    // to convert a hit into a PENALTY instead of a kill. The lead tints/marks it
    // off `getEnemy(def).faction === 'civilian'`.
    faction: 'civilian',
    name: 'BLUEFOR Trooper',
    hp: 30,
    speed: 2.6,
    contactDamage: 0,
    bounty: 0,
    widthM: 0.65,
    heightM: 1.8,
    headMult: 1,
    tier: 'grunt',
    // Reuses the upright runner silhouette mesh; the lead recolors it to BLUEFOR
    // via faction (NOT skin), so it reads as an ally, not a runner.
    skin: 'runner',
    // Marches up the lane with the advance; never strikes (contactDamage 0).
    behavior: 'march',
  },
} as const);

export function getEnemy(id: EnemyId): EnemyDef {
  return ENEMIES[id];
}

/** The enemy's tactical profile, defaulting to `march` when unset. */
export function getBehavior(def: EnemyDef): EnemyBehavior {
  return def.behavior ?? 'march';
}

/** True if the enemy is a ranged attacker (fires projectiles at the gunner). */
export function isRanged(def: EnemyDef): boolean {
  const b = getBehavior(def);
  return b === 'shoot' || b === 'lob';
}

/**
 * True if this unit is a FRIENDLY (BLUEFOR) — a `civilian`-faction ally that
 * walks the lane but must NOT be shot. The single source of friend/foe truth:
 * FiringSystem branches on it to penalise a hit, EnemySystem skips its strike,
 * and the render layer tints/marks it. Reads the faction field, never the id.
 */
export function isFriendly(def: EnemyDef): boolean {
  return def.faction === 'civilian';
}

export function zoneMult(def: EnemyDef, zone: BodyZone): number {
  if (zone === 'head') return def.headMult;
  if (zone === 'legs') return 0.6;
  return 1;
}
